import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Circle, Heart, Sun, Wind, MessageCircle, Eye, Crown, Sparkles, Clock, Calendar, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

interface ChakraHealingSession {
  id: string;
  created_at: string;
  chakras_targeted: string[];
  energy_before: number;
  energy_after: number;
  emotional_state_before: string | null;
  emotional_state_after: string | null;
  practices_used: string[] | null;
  session_duration_minutes: number | null;
  session_intention: string | null;
  notes: string | null;
}

interface ChakraHealingModuleProps {
  onDataChange?: () => void;
}

const CHAKRAS = [
  {
    id: 'root',
    name: 'Root',
    sanskrit: 'Muladhara',
    color: 'bg-red-500',
    borderColor: 'border-red-500',
    icon: Circle,
    location: 'Base of spine',
    themes: {
      body: 'Legs, feet, bones, adrenals, survival instincts',
      emotions: 'Safety, security, trust, belonging',
      mind: 'Grounding, stability, presence',
      relationships: 'Family of origin, ancestral patterns, tribe',
      spiritual: 'Connection to earth, embodiment, right to exist'
    },
    shadowWork: 'Fear, anxiety, disconnection, abandonment wounds',
    practices: ['Grounding meditation', 'Body scan', 'Walking in nature', 'Root chakra breathwork']
  },
  {
    id: 'sacral',
    name: 'Sacral',
    sanskrit: 'Svadhisthana',
    color: 'bg-orange-500',
    borderColor: 'border-orange-500',
    icon: Heart,
    location: 'Lower abdomen',
    themes: {
      body: 'Hips, lower back, reproductive organs, fluidity',
      emotions: 'Pleasure, creativity, sensuality, emotional flow',
      mind: 'Flexibility, adaptability, creative thinking',
      relationships: 'Intimacy, boundaries, healthy desire',
      spiritual: 'Right to feel, creative expression, sacred sexuality'
    },
    shadowWork: 'Guilt, shame, emotional numbness, boundary violations',
    practices: ['Hip-opening stretches', 'Creative expression', 'Water meditation', 'Emotional release breathwork']
  },
  {
    id: 'solar',
    name: 'Solar Plexus',
    sanskrit: 'Manipura',
    color: 'bg-yellow-500',
    borderColor: 'border-yellow-500',
    icon: Sun,
    location: 'Upper abdomen',
    themes: {
      body: 'Digestive system, core strength, personal power',
      emotions: 'Confidence, self-worth, willpower, courage',
      mind: 'Personal identity, decision-making, mental clarity',
      relationships: 'Healthy assertiveness, equal partnerships',
      spiritual: 'Right to act, personal sovereignty, inner fire'
    },
    shadowWork: 'Shame, powerlessness, people-pleasing, control issues',
    practices: ['Core-strengthening', 'Breath of fire', 'Affirmations for self-worth', 'Solar plexus meditation']
  },
  {
    id: 'heart',
    name: 'Heart',
    sanskrit: 'Anahata',
    color: 'bg-green-500',
    borderColor: 'border-green-500',
    icon: Heart,
    location: 'Centre of chest',
    themes: {
      body: 'Heart, lungs, arms, hands, immune system',
      emotions: 'Love, compassion, grief, forgiveness',
      mind: 'Balance between giving and receiving, self-compassion',
      relationships: 'Unconditional love, healthy attachment, empathy',
      spiritual: 'Right to love and be loved, unity consciousness'
    },
    shadowWork: 'Grief, heartbreak, codependency, emotional walls',
    practices: ['Heart-opening meditation', 'Loving-kindness practice', 'Grief ritual', 'Heart coherence breathing']
  },
  {
    id: 'throat',
    name: 'Throat',
    sanskrit: 'Vishuddha',
    color: 'bg-blue-500',
    borderColor: 'border-blue-500',
    icon: MessageCircle,
    location: 'Throat',
    themes: {
      body: 'Throat, neck, thyroid, mouth, ears',
      emotions: 'Authentic expression, speaking truth, listening',
      mind: 'Communication, creativity, clarity of thought',
      relationships: 'Honest dialogue, active listening, boundaries',
      spiritual: 'Right to speak and be heard, creative voice'
    },
    shadowWork: 'Fear of speaking up, lying, gossip, suppressed voice',
    practices: ['Chanting/toning', 'Journaling', 'Throat chakra meditation', 'Authentic expression practice']
  },
  {
    id: 'third-eye',
    name: 'Third Eye',
    sanskrit: 'Ajna',
    color: 'bg-indigo-500',
    borderColor: 'border-indigo-500',
    icon: Eye,
    location: 'Between eyebrows',
    themes: {
      body: 'Eyes, brain, pituitary gland, nervous system',
      emotions: 'Intuition, insight, inner knowing, perception',
      mind: 'Wisdom, discernment, clarity, vision',
      relationships: 'Seeing others clearly, empathic attunement',
      spiritual: 'Right to see, psychic awareness, inner vision'
    },
    shadowWork: 'Illusion, denial, over-intellectualisation, spiritual bypass',
    practices: ['Third eye meditation', 'Visualisation', 'Candle gazing', 'Intuition development']
  },
  {
    id: 'crown',
    name: 'Crown',
    sanskrit: 'Sahasrara',
    color: 'bg-purple-500',
    borderColor: 'border-purple-500',
    icon: Crown,
    location: 'Top of head',
    themes: {
      body: 'Brain, pineal gland, nervous system, consciousness',
      emotions: 'Bliss, peace, transcendence, spiritual connection',
      mind: 'Higher consciousness, divine wisdom, meaning',
      relationships: 'Connection to all beings, universal love',
      spiritual: 'Right to know, divine connection, enlightenment'
    },
    shadowWork: 'Spiritual disconnection, materialism, nihilism, spiritual addiction',
    practices: ['Silent meditation', 'Prayer', 'Crown chakra meditation', 'Sitting in the Power']
  }
];

const AVAILABLE_PRACTICES = [
  { id: 'meditation', label: 'Meditation' },
  { id: 'breathwork', label: 'Breathwork' },
  { id: 'sitting-in-power', label: 'Sitting in the Power' },
  { id: 'body-scan', label: 'Body Scan' },
  { id: 'grounding', label: 'Grounding Practice' },
  { id: 'visualisation', label: 'Visualisation' },
  { id: 'journaling', label: 'Journaling' },
  { id: 'movement', label: 'Gentle Movement/Yoga' },
  { id: 'sound-healing', label: 'Sound/Chanting' },
  { id: 'nature', label: 'Time in Nature' },
  { id: 'energy-clearing', label: 'Energy Clearing' },
  { id: 'affirmations', label: 'Affirmations' }
];

const EMOTIONAL_TAGS = [
  'Anxious', 'Calm', 'Scattered', 'Grounded', 'Heavy', 'Light', 
  'Blocked', 'Open', 'Tired', 'Energised', 'Sad', 'Peaceful',
  'Overwhelmed', 'Centred', 'Disconnected', 'Present', 'Numb', 'Alive'
];

export default function ChakraHealingModule({ onDataChange }: ChakraHealingModuleProps) {
  const { toast } = useToast();
  const [selectedChakras, setSelectedChakras] = useState<string[]>([]);
  const [energyBefore, setEnergyBefore] = useState(5);
  const [energyAfter, setEnergyAfter] = useState(5);
  const [emotionalStateBefore, setEmotionalStateBefore] = useState<string[]>([]);
  const [emotionalStateAfter, setEmotionalStateAfter] = useState<string[]>([]);
  const [practicesUsed, setPracticesUsed] = useState<string[]>([]);
  const [sessionDuration, setSessionDuration] = useState(15);
  const [sessionIntention, setSessionIntention] = useState('');
  const [notes, setNotes] = useState('');
  const [recentSessions, setRecentSessions] = useState<ChakraHealingSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadRecentSessions();
  }, []);

  const loadRecentSessions = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('chakra_healing_sessions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);

    if (!error && data) {
      setRecentSessions(data as ChakraHealingSession[]);
    }
  };

  const toggleChakra = (chakraId: string) => {
    setSelectedChakras(prev => 
      prev.includes(chakraId) 
        ? prev.filter(c => c !== chakraId)
        : [...prev, chakraId]
    );
  };

  const toggleEmotionBefore = (emotion: string) => {
    setEmotionalStateBefore(prev =>
      prev.includes(emotion)
        ? prev.filter(e => e !== emotion)
        : [...prev, emotion]
    );
  };

  const toggleEmotionAfter = (emotion: string) => {
    setEmotionalStateAfter(prev =>
      prev.includes(emotion)
        ? prev.filter(e => e !== emotion)
        : [...prev, emotion]
    );
  };

  const togglePractice = (practiceId: string) => {
    setPracticesUsed(prev =>
      prev.includes(practiceId)
        ? prev.filter(p => p !== practiceId)
        : [...prev, practiceId]
    );
  };

  const handleSaveSession = async () => {
    if (selectedChakras.length === 0) {
      toast({
        title: "Please select at least one chakra",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast({
        title: "Please sign in to save sessions",
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    const { error } = await supabase
      .from('chakra_healing_sessions')
      .insert({
        user_id: user.id,
        chakras_targeted: selectedChakras,
        energy_before: energyBefore,
        energy_after: energyAfter,
        emotional_state_before: emotionalStateBefore.join(', '),
        emotional_state_after: emotionalStateAfter.join(', '),
        practices_used: practicesUsed,
        session_duration_minutes: sessionDuration,
        session_intention: sessionIntention || null,
        notes: notes || null
      });

    if (error) {
      toast({
        title: "Error saving session",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Session logged",
        description: "Your chakra healing session has been saved."
      });
      
      // Reset form
      setSelectedChakras([]);
      setEnergyBefore(5);
      setEnergyAfter(5);
      setEmotionalStateBefore([]);
      setEmotionalStateAfter([]);
      setPracticesUsed([]);
      setSessionDuration(15);
      setSessionIntention('');
      setNotes('');
      setShowForm(false);
      
      loadRecentSessions();
      onDataChange?.();
    }
    
    setLoading(false);
  };

  const getSelectedChakraInfo = () => {
    return CHAKRAS.filter(c => selectedChakras.includes(c.id));
  };

  const getChakraById = (id: string) => CHAKRAS.find(c => c.id === id);

  return (
    <div className="space-y-6">
      {/* Introduction */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            Chakra Healing
          </CardTitle>
          <CardDescription className="text-sm leading-relaxed">
            Work with your energy centres in a grounded, trauma-informed way. The chakras reflect 
            patterns in body, emotions, mind, relationships, and spiritual growth. This is not about 
            forcing energy or "fixing" yourself-it's about gentle awareness and compassionate attention.
          </CardDescription>
        </CardHeader>
      </Card>

      {!showForm ? (
        <>
          {/* Chakra Selector */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Select Chakra(s) to Work With</CardTitle>
              <CardDescription className="text-xs">
                Tap to select one or more chakras for today's practice
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3">
                {CHAKRAS.map((chakra) => {
                  const Icon = chakra.icon;
                  const isSelected = selectedChakras.includes(chakra.id);
                  
                  return (
                    <div
                      key={chakra.id}
                      onClick={() => toggleChakra(chakra.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                        isSelected 
                          ? `${chakra.borderColor} bg-card shadow-md` 
                          : 'border-border/50 hover:border-border'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full ${chakra.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-foreground">{chakra.name}</h4>
                            <span className="text-xs text-muted-foreground">({chakra.sanskrit})</span>
                            <span className="text-xs text-muted-foreground ml-auto">{chakra.location}</span>
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            <strong>Themes:</strong> {chakra.themes.emotions}
                          </p>
                          {isSelected && (
                            <div className="mt-2 pt-2 border-t border-border/50 space-y-1 text-xs text-muted-foreground">
                              <p><strong>Body:</strong> {chakra.themes.body}</p>
                              <p><strong>Shadow work:</strong> {chakra.shadowWork}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Today's Focus Panel */}
          {selectedChakras.length > 0 && (
            <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sun className="h-4 w-4 text-primary" />
                  Today's Focus
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {getSelectedChakraInfo().map(chakra => (
                    <Badge key={chakra.id} className={`${chakra.color} text-white`}>
                      {chakra.name}
                    </Badge>
                  ))}
                </div>
                
                <div className="space-y-3">
                  {getSelectedChakraInfo().map(chakra => (
                    <div key={chakra.id} className="text-sm">
                      <h5 className="font-medium text-foreground mb-1">{chakra.name} Chakra</h5>
                      <ul className="text-xs text-muted-foreground space-y-1 ml-4 list-disc">
                        <li><strong>Relationships:</strong> {chakra.themes.relationships}</li>
                        <li><strong>Spiritual:</strong> {chakra.themes.spiritual}</li>
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Suggested Practices */}
                <div className="pt-2 border-t border-border/50">
                  <h5 className="text-sm font-medium text-foreground mb-2">Suggested Practices</h5>
                  <div className="flex flex-wrap gap-1">
                    {getSelectedChakraInfo().flatMap(c => c.practices).filter((p, i, arr) => arr.indexOf(p) === i).slice(0, 6).map(practice => (
                      <Badge key={practice} variant="secondary" className="text-xs">
                        {practice}
                      </Badge>
                    ))}
                  </div>
                </div>

                <Button onClick={() => setShowForm(true)} className="w-full mt-4">
                  Begin Healing Session
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Recent Sessions */}
          {recentSessions.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  Recent Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-3">
                    {recentSessions.map((session) => (
                      <div key={session.id} className="p-3 rounded-lg bg-muted/30 border border-border/50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(session.created_at), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            <TrendingUp className="h-3 w-3 text-primary" />
                            <span className="text-muted-foreground">
                              {session.energy_before} → {session.energy_after}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {session.chakras_targeted.map(chakraId => {
                            const chakra = getChakraById(chakraId);
                            return chakra ? (
                              <Badge key={chakraId} className={`${chakra.color} text-white text-xs`}>
                                {chakra.name}
                              </Badge>
                            ) : null;
                          })}
                        </div>
                        {session.session_intention && (
                          <p className="text-xs text-muted-foreground mt-2 italic">
                            "{session.session_intention}"
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        /* Session Logging Form */
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Log Your Healing Session</CardTitle>
            <CardDescription className="text-xs">
              Working with: {getSelectedChakraInfo().map(c => c.name).join(', ')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Session Intention */}
            <div className="space-y-2">
              <Label className="text-sm">Session Intention (optional)</Label>
              <Textarea
                placeholder="What do you hope to explore or release today? e.g., 'I want to feel more grounded' or 'I'm working with grief in my heart'"
                value={sessionIntention}
                onChange={(e) => setSessionIntention(e.target.value)}
                className="min-h-[60px] text-sm"
              />
            </div>

            <Separator />

            {/* Energy Before */}
            <div className="space-y-3">
              <Label className="text-sm">Energy Level Before (1-10)</Label>
              <div className="flex items-center gap-4">
                <span className="text-xs text-muted-foreground w-16">Low: 1</span>
                <Slider
                  value={[energyBefore]}
                  onValueChange={(v) => setEnergyBefore(v[0])}
                  min={1}
                  max={10}
                  step={1}
                  className="flex-1"
                />
                <span className="text-xs text-muted-foreground w-16 text-right">High: 10</span>
              </div>
              <div className="text-center">
                <Badge variant="outline" className="text-lg">{energyBefore}</Badge>
              </div>
            </div>

            {/* Emotional State Before */}
            <div className="space-y-2">
              <Label className="text-sm">Emotional State Before</Label>
              <div className="flex flex-wrap gap-1">
                {EMOTIONAL_TAGS.map(emotion => (
                  <Badge
                    key={emotion}
                    variant={emotionalStateBefore.includes(emotion) ? "default" : "outline"}
                    className="cursor-pointer text-xs"
                    onClick={() => toggleEmotionBefore(emotion)}
                  >
                    {emotion}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            {/* Practices Used */}
            <div className="space-y-2">
              <Label className="text-sm">Practices Used</Label>
              <div className="grid grid-cols-2 gap-2">
                {AVAILABLE_PRACTICES.map(practice => (
                  <div key={practice.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={practice.id}
                      checked={practicesUsed.includes(practice.id)}
                      onCheckedChange={() => togglePractice(practice.id)}
                    />
                    <Label htmlFor={practice.id} className="text-xs cursor-pointer">
                      {practice.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label className="text-sm">Session Duration (minutes)</Label>
              <div className="flex items-center gap-2">
                {[5, 10, 15, 20, 30, 45, 60].map(mins => (
                  <Badge
                    key={mins}
                    variant={sessionDuration === mins ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => setSessionDuration(mins)}
                  >
                    {mins}
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            {/* Energy After */}
            <div className="space-y-3">
              <Label className="text-sm">Energy Level After (1-10)</Label>
              <div className="flex items-center gap-4">
                <span className="text-xs text-muted-foreground w-16">Low: 1</span>
                <Slider
                  value={[energyAfter]}
                  onValueChange={(v) => setEnergyAfter(v[0])}
                  min={1}
                  max={10}
                  step={1}
                  className="flex-1"
                />
                <span className="text-xs text-muted-foreground w-16 text-right">High: 10</span>
              </div>
              <div className="text-center">
                <Badge variant="outline" className="text-lg">{energyAfter}</Badge>
              </div>
            </div>

            {/* Emotional State After */}
            <div className="space-y-2">
              <Label className="text-sm">Emotional State After</Label>
              <div className="flex flex-wrap gap-1">
                {EMOTIONAL_TAGS.map(emotion => (
                  <Badge
                    key={emotion}
                    variant={emotionalStateAfter.includes(emotion) ? "default" : "outline"}
                    className="cursor-pointer text-xs"
                    onClick={() => toggleEmotionAfter(emotion)}
                  >
                    {emotion}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm">Session Notes (optional)</Label>
              <Textarea
                placeholder="Any insights, sensations, or experiences during the session..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[80px] text-sm"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowForm(false)}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleSaveSession}
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Saving...' : 'Save Session'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Helper function for correlations - can be imported elsewhere
export async function getRecentChakraHealingSessions(userId: string, limit: number = 10) {
  const { data, error } = await supabase
    .from('chakra_healing_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching chakra healing sessions:', error);
    return [];
  }

  return data as ChakraHealingSession[];
}
